export type LineStatus = 'correct' | 'incorrect' | 'missing' | null;

export interface IPublisherCounts {
    correct: number;
    incorrect: number;
    missing: number;
    unverified: number;
    /** Active (not removal-requested) lines. */
    total: number;
    /** Removal-requested and still live - what goes in "lines to remove". */
    toRemove: number;
    /** All removal-requested lines, live or not. */
    removed: number;
    staleRemoved: number;
}

export interface ITrackedPublisher {
    _id?: string;
    name: string;
    domain: string;
    appAdsTxtUrl?: string;
    responsiveness: number;
    contactEmail?: string;
    notes?: string;
    /** Internal owners of the relationship. Free text - see the backend schema comment. */
    accountManagers?: string[];
    /**
     * Parked rather than deleted. Absent on anything written before the field was used, which
     * counts as active - hence the `!== false` comparisons rather than truthiness checks.
     */
    isActive?: boolean;
    archivedAt?: string;
    sellerId?: string;
    lastCheckedAt?: string;
    lastCheckError?: string;
    /** Which kind of read failure, and the suggested fix. Absent when the last check worked. */
    lastCheckErrorKind?: string;
    lastCheckErrorLabel?: string;
    lastCheckErrorAction?: string;
    updateListLastGeneratedAt?: string;
    created?: number;
    updated?: number;
    /** Only present on the list endpoint, which aggregates them. */
    counts?: IPublisherCounts;
}

export const initialTrackedPublisher: ITrackedPublisher = {
    _id: null,
    name: '',
    domain: '',
    appAdsTxtUrl: '',
    responsiveness: 3,
    contactEmail: '',
    notes: '',
    sellerId: '',
    accountManagers: [],
};

export interface IStatusHistoryEntry {
    date: string;
    from: LineStatus;
    to: LineStatus;
}

export interface ITrackedLine {
    _id?: string;
    publisherId?: string;
    domain: string;
    pubId: string;
    relationship: string;
    certId?: string;
    status: LineStatus;
    actualRelationship?: string | null;
    lastCheckedAt?: string;
    statusChangedAt?: string;
    statusHistory?: IStatusHistoryEntry[];
    isRemoved?: boolean;
    removedAt?: string;
    foundInFile?: boolean | null;
    sourceDemandId?: string;
    sourceDescription?: string;
    /** Ad-ops' own note of which demand this line is for. Editable, unlike sourceDescription. */
    demand?: string;
    /** Added by the detail endpoint for removed lines: nothing known since removal. */
    needsRecheck?: boolean;
}

export interface IAnalyzeStats {
    changed: number;
    resolved: number;
    newlyBroken: number;
    changedType: number;
    removalsConfirmed: number;
    resolvedLines?: ITrackedLine[];
    brokenLines?: ITrackedLine[];
}

export interface ICheckResult {
    ok: boolean;
    publisherId: string;
    name: string;
    stats?: IAnalyzeStats;
    summary: string;
    /** Current state, not just what changed - see the backend interface for why. */
    currentCounts?: { correct: number; incorrect: number; missing: number; unverified: number };
    parsedEntryCount?: number;
    error?: string;
    failureKind?: string;
    failureLabel?: string;
    failureAction?: string;
}

/** A persisted, finished sweep — the durable version of what the summary modal showed. */
export interface ISweepLog {
    _id: string;
    startedAt: string;
    finishedAt?: string;
    /**
     * True between start and completion. A log still flagged running while no sweep is in
     * flight means that run was interrupted — almost always a redeploy or restart.
     */
    running?: boolean;
    total: number;
    checked: number;
    failed: number;
    error?: string;
    results: {
        publisherId: string;
        name: string;
        ok: boolean;
        summary?: string;
        failureKind?: string;
        failureLabel?: string;
        failureAction?: string;
        error?: string;
        changed?: number;
    }[];
}

export interface ISweepStatus {
    sweepId: string;
    running: boolean;
    startedAt: string;
    finishedAt?: string;
    /** 0 until the publisher list has been read on the server. */
    total: number;
    /** Advances per batch, so it moves in steps rather than one at a time. */
    completed: number;
    checked: number;
    failed: number;
    results: ICheckResult[];
    error?: string;
}

export interface IUpdateList {
    publisher: {
        _id: string;
        name: string;
        domain: string;
        contactEmail?: string;
        responsiveness?: number;
        lastCheckedAt?: string;
    };
    toAdd: ITrackedLine[];
    toCorrect: ITrackedLine[];
    toRemove: ITrackedLine[];
    staleRemoved: ITrackedLine[];
    hasStaleWarning: boolean;
}

export type ComposedLineKind = 'own' | 'buyer' | 'extra';

export interface IComposedLine {
    line: string;
    domain: string;
    pubId: string;
    relationship: string;
    certId?: string;
    kind: ComposedLineKind;
    demandName?: string;
    demandId?: string;
    description?: string;
}

export interface IComposeLinesResult {
    ownLine: IComposedLine | null;
    ownLineUnavailableReason?: string;
    buyerLines: IComposedLine[];
    extraLines: IComposedLine[];
}

export interface IImportResult {
    added: number;
    /** Skipped because the publisher already had that exact line. */
    skipped: number;
    /** Rows in the file that couldn't be understood - reported apart from duplicates. */
    invalid?: number;
    /** Data rows the file offered, so the UI can say "understood 3 of 40". */
    rowsRead?: number;
}

export interface IImportToAllResult extends IImportResult {
    perPublisher: { publisherId: string; name: string; added: number; skipped: number }[];
}

export interface IActivateLineResult {
    restored: number;
    added: number;
    skipped: number;
}

/** One publisher's state for a given line, shown when a row is expanded. */
export interface ILineSpecPublisher {
    publisherId: string;
    name?: string;
    publisherDomain?: string;
    status: LineStatus;
    isRemoved?: boolean;
    foundInFile?: boolean | null;
    lastCheckedAt?: string;
    /** This publisher's own annotation, so differing values stay attributable. */
    demand?: string;
}

/** One row of the all-lines view: a distinct line spec across the whole seat. */
export interface IDistinctLineSpec {
    _id: { domain: string; pubId: string; relationship: string };
    certId?: string;
    activeCount: number;
    removedCount: number;
    correct: number;
    incorrect: number;
    missing: number;
    publisherIds: string[];
    /** Per-publisher breakdown, so a row can be expanded instead of just counted. */
    publishers: ILineSpecPublisher[];
    /** Every distinct demand across the publishers carrying this line; empties stripped. */
    demands?: string[];
}

/** One old line, with the publisher that still has it. */
export interface IOldLineRow {
    _id: string;
    publisherId: string;
    publisherName: string;
    publisherDomain: string;
    domain: string;
    pubId: string;
    relationship: string;
    certId?: string;
    removedAt?: string;
    lastCheckedAt?: string;
    foundInFile?: boolean | null;
    needsRecheck?: boolean;
    demand?: string;
}

export const RESPONSIVENESS_LABELS: Record<number, string> = {
    1: '1 - very slow',
    2: '2 - slow',
    3: '3 - average',
    4: '4 - responsive',
    5: '5 - very responsive',
};

/**
 * Fallback labels only.
 *
 * The server sends the label it used (`lastCheckErrorLabel` / `failureLabel`), and that's what
 * should be displayed. This map exists solely for records written before that field did — it
 * used to be the display source, which meant changing the wording server-side left the old
 * text on screen. Read `failureLabelFor` rather than reaching in here directly.
 */
const FALLBACK_FAILURE_LABELS: Record<string, string> = {
    'no-file': 'No app-ads.txt (404)',
    'domain-unresolved': "Domain doesn't resolve",
    blocked: 'Blocked',
    'too-large': 'File too large',
    timeout: 'Timed out',
    other: "Couldn't read",
};

/** Prefers the server's own wording, falling back for pre-existing records. */
export function failureLabelFor(label?: string, kind?: string): string {
    return label || FALLBACK_FAILURE_LABELS[kind || 'other'] || "Couldn't read";
}

export function statusLabel(status: LineStatus): string {
    if (status === 'correct') return 'Live & correct';
    if (status === 'incorrect') return 'Wrong type';
    if (status === 'missing') return 'Not found';
    return 'Unverified';
}

/**
 * The URL a check will actually fetch: the override when there is one, otherwise
 * <domain>/app-ads.txt. A scheme is added when missing so the value is unambiguous.
 *
 * Nothing about the shape is enforced. Publishers host their file in genuinely odd places -
 * one in the real data sits at adstxt.guru/hosting/<token>/ - and refusing those meant ad-ops
 * couldn't track the publisher at all. A wrong URL simply fails its next check and shows up in
 * the Read status column, which is ad-ops' to fix.
 */
export function resolveAppAdsTxtUrl(publisher?: {
    domain?: string;
    appAdsTxtUrl?: string;
}): string {
    const override = (publisher?.appAdsTxtUrl || '').trim();
    const domain = (publisher?.domain || '').trim();
    const raw = override || (domain ? `${domain}/app-ads.txt` : '');
    if (!raw) return '';
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;
}

// ============================= onboarding bulk upload =============================

/**
 * A problem with one row. Same shape as the lines importers' IRowIssue plus the publisher it
 * belonged to - with eleven publishers interleaved in one file, a bare row number isn't enough
 * to find it.
 */
export interface IOnboardingIssue {
    /** 1-based, counting the way the user's file does. */
    row: number;
    text: string;
    reason: string;
    publisher?: string;
}

/** One publisher field the sheet disagrees with PAPA2 about, and what the import will do. */
export interface IOnboardingDiffField {
    field: 'name' | 'appAdsTxtUrl' | 'sellerId' | 'accountManagers';
    inPapa: string;
    inFile: string;
    /** 'keep' leaves PAPA2 alone (the default); 'take' writes the sheet's value. */
    resolution: 'keep' | 'take';
}

/** What the import would do to one publisher. */
export interface IOnboardingPublisherPlan {
    domain: string;
    name: string;
    publisherId?: string;
    isNew: boolean;
    linesToAdd: number;
    linesAlreadyPresent: number;
    /** Listed in the sheet but already on the publisher as removed: skipped, never restored. */
    linesRemoved: number;
    fields: IOnboardingDiffField[];
}

export interface IOnboardingPreview {
    rowsRead: number;
    /** Any of these and nothing is imported. */
    blocking: IOnboardingIssue[];
    /** Worth seeing, but the import goes ahead. */
    advisory: IOnboardingIssue[];
    publishers: IOnboardingPublisherPlan[];
    totals: {
        publishersToCreate: number;
        publishersExisting: number;
        linesToAdd: number;
        linesAlreadyPresent: number;
        linesRemoved: number;
    };
}

export interface IOnboardingImportResult extends IOnboardingPreview {
    created: { publishers: number; lines: number };
}

/** Merge-preview choices, keyed by publisher domain then field. */
export type OnboardingResolutions = Record<string, Record<string, 'keep' | 'take'>>;
